/**
 * NativeAddProjectApp — the Add Project form rendered standalone for hosting
 * inside a native iOS `.sheet` (a WKWebView loads `?embed=add-project`).
 *
 * The native sheet supplies the real system chrome (grab handle, detents,
 * swipe-to-dismiss); this renders just the form plus a thin header (back /
 * close + title) driven by the form's existing `add-project:*` event
 * protocol. Lives inside `AppProviders` (App.tsx), so Auth / Billing / Data /
 * Realtime are all available — same as NativeSidePanelApp.
 *
 * Dismissal and "created" are reported to the native shell via the
 * `dismissAddProject` bridge message; the native side closes the sheet and
 * refreshes the main webview.
 */

import { Suspense, useEffect, useState } from 'react';
import { ChevronLeft, X } from '@/shared/ui/icons';
import { useAuth } from '../platform/auth/AuthContext';
import { ViewLoader } from './ViewLoader';
import { NativeBridge } from '../services/NativeBridge';
import { lazyChunk } from '../utils/lazyChunk';

const AddProjectForm = lazyChunk(
  () => import('../features/projects/add-project-form/AddProjectFormRoute'),
  (module) => module.AddProjectForm,
);

export function NativeAddProjectApp() {
  const auth = useAuth();
  // The `add-project:state-change` payload carries more fields for the
  // form footer; this host's own chrome only needs the leading control.
  const [hasPreviousTab, setHasPreviousTab] = useState(false);

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<{ hasPreviousTab?: boolean } | undefined>).detail;
      setHasPreviousTab(Boolean(detail?.hasPreviousTab));
    };
    window.addEventListener('add-project:state-change', onChange);
    window.dispatchEvent(new CustomEvent('add-project:request-state'));
    return () => window.removeEventListener('add-project:state-change', onChange);
  }, []);

  const dismiss = (payload: Record<string, unknown> = {}) =>
    NativeBridge.send('dismissAddProject', payload);

  if (auth.status === 'unknown' || !auth.user || auth.status !== 'authenticated') {
    return <ViewLoader />;
  }

  return (
    <div className="flex flex-col h-[100dvh] bg-background font-apple">
      {/* Thin header below the native grab handle (small top inset clears the
          system drag indicator). Back a step, or close on step 1. Forward motion
          (Continue / Create) lives in the form's own pinned footer. */}
      <div className="flex items-center gap-2 px-2 h-12 shrink-0 pt-2">
        <div className="flex w-20 justify-start">
          {hasPreviousTab ? (
            <button
              type="button"
              aria-label="Previous step"
              onClick={() => window.dispatchEvent(new CustomEvent('add-project:action:previous'))}
              className="h-9 w-9 flex items-center justify-center rounded-full text-foreground active:scale-95 transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          ) : (
            <button
              type="button"
              aria-label="Close"
              onClick={() => dismiss()}
              className="h-9 w-9 flex items-center justify-center rounded-full text-foreground active:scale-95 transition"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <h1 className="flex-1 text-center text-[17px] font-semibold text-foreground">
          New Project
        </h1>

        <div className="flex w-20 justify-end" />
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <Suspense fallback={<ViewLoader />}>
          <AddProjectForm
            currentUser={auth.user}
            embeddedInSheet
            onClose={() => dismiss()}
            onProjectCreated={(projectId) => dismiss({ projectId, created: true })}
            // Adding a brand-new contractor mid-form needs the full app; not
            // offered inside the sheet. The EmployerTab hides the affordance
            // when this is undefined.
            onAddMainContractor={undefined}
          />
        </Suspense>
      </div>
    </div>
  );
}
